import React, { useRef, useState } from 'react';

export const MAX_SLIDES = 10;

export const DEFAULT_IMAGE_POSITION = { x: 50, y: 50 }

export const EMPTY_SLIDE = { titleText: '', contentText: '', personName: '', personTitle: '', company: '', fileId: '', ctaText: '', ctaUrl: '', imagePosition: { ...DEFAULT_IMAGE_POSITION } }

const clampPercent = value => Math.min(100, Math.max(0, value))

const isImageFile = file => !!(file && file.meta && file.meta.type && file.meta.type.includes('image'))

// Picks the `object-position` focal point of the widget's 16:9 `object-fit: cover` crop.
function ImagePositioner({ url, position, onChange, t }) {
  const frameRef = useRef(null)
  const imgRef = useRef(null)
  const dragRef = useRef(null)
  const focal = position || DEFAULT_IMAGE_POSITION

  const startDrag = e => {
    const frame = frameRef.current
    const img = imgRef.current
    if (!frame || !img || !img.naturalWidth || !img.naturalHeight) return
    // The overflow `cover` pushes outside the frame is the whole 0-100% travel of
    // object-position, which converts a drag in pixels into percent.
    const scale = Math.max(frame.clientWidth / img.naturalWidth, frame.clientHeight / img.naturalHeight)
    dragRef.current = {
      x: focal.x,
      y: focal.y,
      clientX: e.clientX,
      clientY: e.clientY,
      overflowX: img.naturalWidth * scale - frame.clientWidth,
      overflowY: img.naturalHeight * scale - frame.clientHeight,
    }
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const onDrag = e => {
    const drag = dragRef.current
    if (!drag) return
    // Dragging the image right reveals more of its left side, i.e. a smaller percentage.
    const dx = drag.overflowX > 0 ? ((e.clientX - drag.clientX) / drag.overflowX) * 100 : 0
    const dy = drag.overflowY > 0 ? ((e.clientY - drag.clientY) / drag.overflowY) * 100 : 0
    onChange({ x: Math.round(clampPercent(drag.x - dx)), y: Math.round(clampPercent(drag.y - dy)) })
  }

  const endDrag = e => {
    dragRef.current = null
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
  }

  return (
    <div className="widget-image-position">
      <div className="widget-image-position-hint">
        {t('widget.imagePositionHint') || 'Kuva rajataan 16:9-kuvasuhteeseen. Vedä kuvaa asetellaksesi sen kehyksen sisällä.'}
      </div>
      <div
        className="widget-image-position-frame"
        ref={frameRef}
        onPointerDown={startDrag}
        onPointerMove={onDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <img
          ref={imgRef}
          src={url}
          alt=""
          draggable={false}
          style={{ objectPosition: `${focal.x}% ${focal.y}%` }}
        />
      </div>
      <button
        type="button"
        className="widget-image-position-reset"
        onClick={() => onChange({ ...DEFAULT_IMAGE_POSITION })}
      >
        {t('widget.imagePositionReset') || 'Keskitä'}
      </button>
    </div>
  )
}

function MediaFile({ file, isSelected, onChange }) {
  const isVideo = file.meta && file.meta.type && file.meta.type.includes('video')
  const thumb = isVideo ? (file.meta.thumbnail || null) : (file.url || null)

  return (
    <div className={`content-file${isSelected ? ' content-file--selected' : ''}`}>
      <button className="content-file-preview" onClick={() => onChange(isSelected ? '' : file.id)}>
        {thumb ? (
          <div className="content-file-preview-thumbnail"><img src={thumb} alt={file.filename} /></div>
        ) : (
          <div className="content-file-preview-icon"><i className={`fa fa-${isVideo ? 'video' : 'image'}`} /></div>
        )}
        <div className="content-file-preview-name"><span>{file.filename}</span></div>
      </button>
      {isSelected && (
        <div className="content-file-selected-indicator"><i className="fa fa-check" /></div>
      )}
    </div>
  )
}

function MediaPicker({ fileId, onChange, mediaGroups, mediaLoading, t }) {
  if (mediaLoading) return <div className="form-hint"><i className="fa fa-spin fa-spinner" /></div>
  if (mediaGroups.length === 0) return <div className="form-hint">{t('widget.mediaEmpty') || 'No images found in your organization materials.'}</div>

  return (
    <div className="widget-media-groups">
      {mediaGroups.map(group => (
        <div className="widget-media-group" key={group.id}>
          <div className="widget-media-group-title">{group.label || (t('widget.mediaGroupUntitled') || 'Muu aineisto')}</div>
          <div className="content-file-group-list">
            {group.files.map(file => (
              <MediaFile key={file.id} file={file} isSelected={file.id === fileId} onChange={onChange} />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function SlideCard({ slide, index, total, onChange, onMoveUp, onMoveDown, onRemove, mediaGroups, mediaFiles, mediaLoading, t }) {
  const [expanded, setExpanded] = useState(true)
  const set = (key, val) => onChange({ ...slide, [key]: val })
  const selectedFile = (mediaFiles || []).find(file => file.id === slide.fileId) || null

  return (
    <div className="widget-slide-card">
      <div className="widget-slide-card-header" onClick={() => setExpanded(e => !e)}>
        <span className="widget-slide-card-label">
          {t('widget.slide') || 'Slide'} {index + 1}
          {slide.titleText && <span className="widget-slide-card-hint"> — {slide.titleText}</span>}
        </span>
        <span className="widget-slide-card-controls" onClick={e => e.stopPropagation()}>
          <button className="widget-slide-move-btn" onClick={onMoveUp} disabled={index === 0} title="Move up">↑</button>
          <button className="widget-slide-move-btn" onClick={onMoveDown} disabled={index === total - 1} title="Move down">↓</button>
          {total > 1 && (
            <button className="widget-slide-remove-btn" onClick={onRemove} title="Remove">×</button>
          )}
          <span className="widget-slide-toggle">{expanded ? '▲' : '▼'}</span>
        </span>
      </div>
      {expanded && (
        <div className="widget-slide-card-body">
          <div className="widget-form-field">
            <label>{t('widget.fieldTitleText') || 'Otsikko'}</label>
            <input type="text" value={slide.titleText} onChange={e => set('titleText', e.target.value)} />
          </div>
          <div className="widget-form-field">
            <label>{t('widget.fieldContentText') || 'Teksti'}</label>
            <textarea value={slide.contentText} onChange={e => set('contentText', e.target.value)} rows={4} />
          </div>
          <div className="widget-form-field">
            <label>{t('widget.fieldPersonName') || 'Person name'}</label>
            <input type="text" value={slide.personName} onChange={e => set('personName', e.target.value)} />
          </div>
          <div className="widget-form-field">
            <label>{t('widget.fieldPersonTitle') || 'Person title'}</label>
            <input type="text" value={slide.personTitle} onChange={e => set('personTitle', e.target.value)} />
          </div>
          <div className="widget-form-field">
            <label>{t('widget.fieldCompany') || 'Company'}</label>
            <input type="text" value={slide.company} onChange={e => set('company', e.target.value)} />
          </div>
          <div className="widget-section-heading">CTA</div>
          <div className="widget-form-field">
            <label>{t('widget.fieldCtaText') || 'CTA Text'}</label>
            <input type="text" value={slide.ctaText} onChange={e => set('ctaText', e.target.value)} placeholder="Continue reading at..." />
          </div>
          <div className="widget-form-field">
            <label>{t('widget.fieldCtaUrl') || 'CTA Link'}</label>
            <input type="text" value={slide.ctaUrl} onChange={e => set('ctaUrl', e.target.value)} placeholder="https://example.com" />
          </div>
          <div className="widget-section-heading">{t('widget.fieldMedia') || 'Media'}</div>
          <MediaPicker
            fileId={slide.fileId}
            // A focal point only means something for the image it was picked on.
            onChange={val => onChange({ ...slide, fileId: val, imagePosition: { ...DEFAULT_IMAGE_POSITION } })}
            mediaGroups={mediaGroups}
            mediaLoading={mediaLoading}
            t={t}
          />
          {isImageFile(selectedFile) && selectedFile.url && (
            <ImagePositioner
              url={selectedFile.url}
              position={slide.imagePosition}
              onChange={val => set('imagePosition', val)}
              t={t}
            />
          )}
        </div>
      )}
    </div>
  )
}

export default function TabSlides({ slides, setSlides, mediaGroups, mediaFiles, mediaLoading, t }) {
  const updateSlide = (index, slide) => {
    const next = slides.slice()
    next[index] = slide
    setSlides(next)
  }

  const moveSlide = (index, direction) => {
    const next = slides.slice()
    const target = index + direction
    if (target < 0 || target >= next.length) return
    ;[next[index], next[target]] = [next[target], next[index]]
    setSlides(next)
  }

  const removeSlide = index => {
    setSlides(slides.filter((_, i) => i !== index))
  }

  const addSlide = () => {
    if (slides.length >= MAX_SLIDES) return
    setSlides([...slides, { ...EMPTY_SLIDE }])
  }

  return (
    <React.Fragment>
      <div className="widget-slide-list">
        {slides.map((slide, i) => (
          <SlideCard
            key={i}
            slide={slide}
            index={i}
            total={slides.length}
            onChange={s => updateSlide(i, s)}
            onMoveUp={() => moveSlide(i, -1)}
            onMoveDown={() => moveSlide(i, 1)}
            onRemove={() => removeSlide(i)}
            mediaGroups={mediaGroups}
            mediaFiles={mediaFiles}
            mediaLoading={mediaLoading}
            t={t}
          />
        ))}
      </div>
      {slides.length < MAX_SLIDES && (
        <button className="widget-add-slide-btn" onClick={addSlide}>
          <i className="fa fa-plus" /> {t('widget.addSlide') || 'Add slide'}
        </button>
      )}
    </React.Fragment>
  )
}
